import { useEffect, useState } from 'react'

import { ConfirmDialog } from '~/components/confirm-dialog'
import { CloseIcon, DeleteIcon, DownloadIcon } from '~/components/icons'
import { useSelection, type Selection } from '~/components/selection-context'
import { useToast } from '~/components/toast-context'
import { Working } from '~/components/working'
import { useWork } from '~/components/use-work'
import { useDataService, useDataState } from '~/data/data-context'
import { saveSetCopy } from '~/lib/download-video'
import { useFacesStep } from '~/components/use-faces-step'
import type { LoggedSet } from '~/data/types'

/**
 * What the header becomes while something is selected — `/specs/app.spec.md`, Selecting. The
 * way out of the selection, how many things are in it, and what can be done to them. It stands
 * where the header's own row stands rather than over it, so the page beneath never moves.
 */
export function SelectionBar({ selection }: { selection: Selection }) {
  const { clear, actions } = useSelection()
  const state = useDataState()
  const service = useDataService()
  const toast = useToast()
  const [confirming, setConfirming] = useState(false)
  const [working, setWorking] = useState(false)
  // Saving the videos is work to wait on, and says so on its own button — the app spec, A press
  // that waits.
  const saving = useWork()
  // One faces step for every video in the selection — `/specs/features/faces.spec.md`.
  const faces = useFacesStep()

  const count = selection.ids.length
  const sets =
    selection.kind === 'set'
      ? selection.ids
          .map((id) => state.sets.find((set) => set.id === id))
          .filter((set): set is LoggedSet => set !== undefined)
      : []
  const filmed = sets.filter((set) => set.hasVideo)

  const remove = async () => {
    if (working) return
    setWorking(true)
    try {
      for (const id of selection.ids) {
        if (selection.kind === 'set') {
          await service.deleteSet(id)
        } else {
          await service.deleteWorkout(id)
        }
      }
      // Said once for the whole selection: one act was asked for, so one answer comes back.
      toast(`${String(count)} ${nameOf(selection.kind, count)} deleted`)
    } finally {
      setWorking(false)
      clear()
    }
  }

  const downloading = () =>
    saving.run(async () => {
      if (working) return
      await downloadAll()
    })

  const downloadAll = () =>
    faces.run(filmed, 'Download', (copies) => {
      let saved = 0
      for (const set of filmed) {
        // Counted by what landed rather than stopping at the first that did not: the rest of
        // the selection is still worth saving.
        const copy = copies.of(set.id)
        if (copy !== null) {
          saveSetCopy(copy, set)
          saved += 1
        }
      }
      toast(
        saved === filmed.length
          ? `${String(saved)} ${saved === 1 ? 'video' : 'videos'} downloaded`
          : `${String(saved)} of ${String(filmed.length)} videos downloaded`,
      )
      return Promise.resolve(saved > 0)
    })

  /*
   * The keys a gallery is worked with: Escape lets the selection go, delete asks to delete it,
   * D saves what was filmed. Each does exactly what its control does, asking included — a key
   * is a quicker way to reach a control, not a way past what the control does first.
   */
  const filmedCount = filmed.length
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Not while a dialog is asking about this very selection: Escape is that dialog's own
      // way out, and answering it is not the same as putting the selection down.
      if (event.defaultPrevented || document.querySelector('[role="dialog"]') !== null) {
        return
      }
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return
      }
      if (event.key === 'Escape') {
        clear()
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }
      // The diary's entries are not deleted from here: what can be done to them is theirs.
      if (selection.kind === 'entry') {
        return
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault()
        setConfirming(true)
        return
      }
      if (event.key.toLowerCase() === 'd' && filmedCount > 0) {
        event.preventDefault()
        void downloading()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  })

  return (
    <>
      <button
        type="button"
        aria-label="Close selection"
        className="-ml-2.5 flex shrink-0 items-center rounded-xl p-2.5 text-ink transition-colors hover:bg-hover"
        onClick={clear}
      >
        <CloseIcon className="h-6 w-6" />
      </button>
      <span className="flex-1 truncate text-lg font-medium">{String(count)} selected</span>
      {/* The page that made the selection says what can be done to it, where it is not a
          selection of pictures. */}
      {selection.kind === 'entry' && actions}
      {selection.kind !== 'entry' && (
        <div className="flex shrink-0 items-center gap-1">
          {filmed.length > 0 && (
            <button
              type="button"
              aria-label="Download"
              aria-busy={saving.working || undefined}
              disabled={working}
              className="relative rounded-full p-2.5 text-ink transition-colors hover:bg-hover disabled:opacity-50"
              onClick={() => {
                void downloading()
              }}
            >
              <Working shown={saving.shown} spinner="size-6">
                <DownloadIcon className="h-6 w-6" />
              </Working>
            </button>
          )}
          <button
            type="button"
            aria-label="Delete"
            disabled={working || saving.working}
            className="rounded-full p-2.5 text-danger transition-colors hover:bg-hover disabled:opacity-50"
            onClick={() => {
              setConfirming(true)
            }}
          >
            <DeleteIcon className="h-6 w-6" />
          </button>
        </div>
      )}

      {faces.dialog}
      {confirming && (
        <ConfirmDialog
          title={`Delete ${String(count)} ${nameOf(selection.kind, count)}?`}
          body={
            selection.kind === 'set'
              ? `${count === 1 ? 'The set goes' : 'The sets go'}, along with the videos of them.`
              : `${count === 1 ? 'The workout goes' : 'The workouts go'}, along with every set in ${count === 1 ? 'it' : 'them'} and the videos of those.`
          }
          confirm="Delete"
          onConfirm={() => {
            setConfirming(false)
            void remove()
          }}
          onDismiss={() => {
            setConfirming(false)
          }}
        />
      )}
    </>
  )
}

function nameOf(kind: Selection['kind'], count: number): string {
  if (kind === 'set') {
    return count === 1 ? 'set' : 'sets'
  }
  if (kind === 'entry') {
    return count === 1 ? 'entry' : 'entries'
  }
  return count === 1 ? 'workout' : 'workouts'
}
